import React, { useState, useRef, useEffect } from 'react';
import { Navbar, Container, Nav, Button, Dropdown, Image } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FaUser, FaSignOutAlt, FaUserEdit, FaChalkboardTeacher, FaGraduationCap, FaSignInAlt, FaUserPlus, FaHome, FaInfoCircle, FaPhone, FaBook, FaChevronDown } from 'react-icons/fa';
import { UserRole } from '../../types';
import './Header.css';

// تعريف UserRole للتأكد من وجوده
const { ADMIN, TEACHER, STUDENT } = UserRole;

const Header: React.FC = () => {
  const { currentUser, userData, logout, signOut, photoURL } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    try {
      if (logout) {
        await logout();
      } else {
        await signOut();
      }
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  // إغلاق القائمة عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuRef]);

  // إغلاق القائمة المنسدلة عند تغيير المسار
  useEffect(() => {
    setExpanded(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  return (
    <Navbar bg="primary" variant="dark" expand="lg" expanded={expanded} sticky="top" className="shadow-sm">
      <Container>
        <Navbar.Brand as={Link} to="/" className="d-flex align-items-center">

          <span className="brand-name me-2">السامي</span>
        </Navbar.Brand>
        
        <Navbar.Toggle 
          aria-controls="navbar-nav" 
          onClick={() => setExpanded(!expanded)}
        />
        
        <Navbar.Collapse id="navbar-nav">
          <Nav className="me-auto">
            <Link 
              to="/" 
              className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
              onClick={() => setExpanded(false)}
            >
              <FaHome className="ms-1" />
              الرئيسية
            </Link>
            
            <Link 
              to="/courses" 
              className={`nav-link ${location.pathname.includes('/courses') ? 'active' : ''}`}
              onClick={() => setExpanded(false)}
            >
              <FaBook className="ms-1" />
              الدورات
            </Link>
            
            <Link 
              to="/teachers" 
              className={`nav-link ${location.pathname.includes('/teachers') ? 'active' : ''}`}
              onClick={() => setExpanded(false)}
            >
              <FaChalkboardTeacher className="ms-1" />
              المعلمون
            </Link>
            
            <Link 
              to="/about" 
              className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}
              onClick={() => setExpanded(false)}
            >
              <FaInfoCircle className="ms-1" />
              عن المنصة
            </Link>
            
            <Link 
              to="/contact" 
              className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
              onClick={() => setExpanded(false)}
            >
              <FaPhone className="ms-1" />
              اتصل بنا
            </Link>
          </Nav>
          
          <div className="d-flex align-items-center mt-3 mt-lg-0">
            {currentUser ? (
              <Dropdown align="end">
                <Dropdown.Toggle as="div" id="user-dropdown" className="custom-dropdown-toggle">
                  <div className="user-profile-dropdown d-flex align-items-center">
                    {photoURL ? (
                      <Image 
                        src={photoURL} 
                        alt="صورة المستخدم" 
                        className="user-avatar" 
                      />
                    ) : (
                      <div className="user-avatar-placeholder bg-primary bg-opacity-10">
                        <FaUser size={20} className="text-primary" />
                      </div>
                    )}
                    
                    <div className="user-info d-none d-md-block">
                      <p className="user-name text-white mb-0">{userData?.name || 'المستخدم'}</p>
                      <small className="user-role text-white-50">
                        {userData?.role === ADMIN && 'مسؤول'}
                        {userData?.role === TEACHER && 'معلم'}
                        {userData?.role === STUDENT && 'طالب'}
                      </small>
                    </div>
                    
                    <FaChevronDown className="ms-2 text-white-50" size={12} />
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu className="user-dropdown-menu">
                  <div className="user-menu-header">
                    <p className="fw-bold mb-0">{userData?.name || 'المستخدم'}</p>
                    <small className="text-muted">{currentUser.email}</small>
                  </div>
                  
                  {/* تعديل مسار الملف الشخصي حسب نوع المستخدم */}
                  {userData?.role === ADMIN && (
                    <Dropdown.Item as={Link} to="/admin/profile" className="user-menu-item">
                      <div className="icon">
                        <FaUserEdit />
                      </div>
                      <span>الملف الشخصي</span>
                    </Dropdown.Item>
                  )}
                  
                  {userData?.role === TEACHER && (
                    <Dropdown.Item as={Link} to="/teacher/profile" className="user-menu-item">
                      <div className="icon">
                        <FaUserEdit />
                      </div>
                      <span>الملف الشخصي</span>
                    </Dropdown.Item>
                  )}
                  
                  {userData?.role === STUDENT && (
                    <Dropdown.Item as={Link} to="/student/profile" className="user-menu-item">
                      <div className="icon">
                        <FaUserEdit />
                      </div>
                      <span>الملف الشخصي</span>
                    </Dropdown.Item>
                  )}
                  
                  {userData?.role === TEACHER && (
                    <Dropdown.Item as={Link} to="/teacher/dashboard" className="user-menu-item">
                      <div className="icon">
                        <FaChalkboardTeacher />
                      </div>
                      <span>لوحة تحكم المعلم</span>
                    </Dropdown.Item>
                  )}
                  
                  {userData?.role === STUDENT && (
                    <Dropdown.Item as={Link} to="/student/dashboard" className="user-menu-item">
                      <div className="icon">
                        <FaGraduationCap />
                      </div>
                      <span>لوحة تحكم الطالب</span>
                    </Dropdown.Item>
                  )}
                  
                  {userData?.role === ADMIN && (
                    <Dropdown.Item as={Link} to="/admin/dashboard" className="user-menu-item">
                      <div className="icon">
                        <FaChalkboardTeacher />
                      </div>
                      <span>لوحة تحكم المسؤول</span>
                    </Dropdown.Item>
                  )}
                  
                  <Dropdown.Divider />
                  
                  <Dropdown.Item onClick={handleLogout} className="user-menu-item text-danger">
                    <div className="icon">
                      <FaSignOutAlt />
                    </div>
                    <span>تسجيل الخروج</span>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="outline-light" className="auth-button">
                    <FaSignInAlt className="icon" />
                    تسجيل الدخول
                  </Button>
                </Link>
                
                <Link to="/register">
                  <Button variant="light" className="auth-button">
                    <FaUserPlus className="icon" />
                    إنشاء حساب
                  </Button>
                </Link>
              </>
            )}
          </div>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Header;